import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Calendar, 
  Users, 
  Star, 
  DollarSign,
  Clock,
  TrendingUp,
  ChevronRight,
  Bell,
  MessageSquare,
  Activity
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { appointmentService, getLocalizedValue } from '@/services/api';

// Status mapping for appointments
const APPOINTMENT_STATUS = {
  1: 'pending',
  2: 'confirmed',
  3: 'in_progress',
  4: 'completed',
  5: 'cancelled',
  6: 'rejected',
  7: 'waiting',
};

// Mock data fallback
const getMockDashboardData = (lang) => ({
  stats: {
    todayAppointments: 8,
    totalPatients: 156,
    rating: 4.9,
    monthlyEarnings: 12500,
  },
  upcomingAppointments: [],
  recentReviews: [
    { id: 1, patient: lang === 'ar' ? 'سارة محمد' : 'Sara Mohammed', rating: 5, comment: lang === 'ar' ? 'طبيب ممتاز ومحترف' : 'Excellent and professional doctor', date: new Date(Date.now() - 1000 * 60 * 60 * 24) },
    { id: 2, patient: lang === 'ar' ? 'عمر خالد' : 'Omar Khaled', rating: 5, comment: lang === 'ar' ? 'شكراً على الرعاية الممتازة' : 'Thank you for the excellent care', date: new Date(Date.now() - 1000 * 60 * 60 * 48) },
  ],
});

const DoctorDashboard = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchDashboardData = async () => {
      try {
        // Fetch dashboard stats and appointments from API
        const [statsResponse, appointmentsResponse] = await Promise.all([
          fetch('https://dactrha-staging.novech-sy.com/api/doctor/dashboard', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Accept': 'application/json',
            }
          }).then(r => r.json()).catch(() => null),
          appointmentService.getMyAppointments().catch(() => null),
        ]);

        // Process stats
        let stats = getMockDashboardData(lang).stats;
        if (statsResponse?.success && statsResponse.data) {
          const statsArray = statsResponse.data;
          stats = {
            todayAppointments: statsArray.find(s => s.description === 'total_appointments')?.value || 0,
            totalPatients: statsArray.find(s => s.description === 'total_doctors')?.value || 0,
            rating: 4.9, // Not in API
            monthlyEarnings: statsArray.find(s => s.description === 'doctor_earning')?.value || 0,
          };
        }

        // Process appointments
        let upcomingAppointments = [];
        if (appointmentsResponse?.data?.data) {
          const rawAppointments = Array.isArray(appointmentsResponse.data.data) 
            ? appointmentsResponse.data.data 
            : [];
          
          // Get upcoming appointments (not cancelled/completed)
          upcomingAppointments = rawAppointments
            .filter(apt => !['cancelled', 'completed', 'rejected'].includes(APPOINTMENT_STATUS[apt.appointment_status_id]))
            .slice(0, 5)
            .map(apt => ({
              id: apt.id,
              patient: {
                name: apt.patient 
                  ? `${getLocalizedValue(apt.patient.first_name, lang)} ${getLocalizedValue(apt.patient.last_name, lang)}`
                  : (lang === 'ar' ? 'مريض' : 'Patient'),
                image: apt.patient?.media?.[0]?.url,
              },
              time: new Date(apt.appointment_at),
              type: apt.online ? (lang === 'ar' ? 'استشارة عبر الإنترنت' : 'Online Consultation') : (lang === 'ar' ? 'زيارة عيادة' : 'Clinic Visit'),
              status: APPOINTMENT_STATUS[apt.appointment_status_id] || 'pending',
            }));
        }

        setDashboardData({
          stats,
          upcomingAppointments,
          recentReviews: getMockDashboardData(lang).recentReviews,
        });
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        setDashboardData(getMockDashboardData(lang));
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isAuthenticated, navigate, lang]);

  const quickActions = [
    { icon: Calendar, label: lang === 'ar' ? 'المواعيد' : 'Appointments', to: '/doctor/appointments', color: 'bg-blue-500' },
    { icon: Users, label: lang === 'ar' ? 'المرضى' : 'Patients', to: '/doctor/patients', color: 'bg-green-500' },
    { icon: Clock, label: lang === 'ar' ? 'الجدول' : 'Schedule', to: '/doctor/schedule', color: 'bg-purple-500' },
    { icon: MessageSquare, label: lang === 'ar' ? 'الرسائل' : 'Messages', to: '/messages', color: 'bg-orange-500' },
  ];

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-4 border-white/20">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback className="bg-teal-800 text-white text-xl">
                  {user?.first_name?.charAt(0) || 'D'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-teal-100 text-sm">
                  {lang === 'ar' ? 'مرحباً،' : 'Welcome back,'}
                </p>
                <h1 className="text-2xl font-bold">
                  {lang === 'ar' ? 'د. ' : 'Dr. '}{user?.first_name} {user?.last_name}
                </h1>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" asChild>
                <Link to="/notifications"><Bell className="h-5 w-5" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <Skeleton className="h-8 w-8 rounded mb-2" />
                  <Skeleton className="h-6 w-16 mb-1" />
                  <Skeleton className="h-4 w-24" />
                </CardContent>
              </Card>
            ))
          ) : (
            <>
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="p-2 rounded-lg bg-blue-100 w-fit mb-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{dashboardData.stats.todayAppointments}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'مواعيد اليوم' : "Today's Appointments"}</p>
                </CardContent>
              </Card>
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="p-2 rounded-lg bg-green-100 w-fit mb-2">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{dashboardData.stats.totalPatients}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'إجمالي المرضى' : 'Total Patients'}</p>
                </CardContent>
              </Card>
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="p-2 rounded-lg bg-amber-100 w-fit mb-2">
                    <Star className="h-5 w-5 text-amber-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{dashboardData.stats.rating}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'التقييم' : 'Rating'}</p>
                </CardContent>
              </Card>
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="p-2 rounded-lg bg-teal-100 w-fit mb-2">
                    <DollarSign className="h-5 w-5 text-teal-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">${dashboardData.stats.monthlyEarnings.toLocaleString()}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'أرباح الشهر' : 'Monthly Earnings'}</p>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-3">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.to}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow"
            >
              <div className={`p-3 rounded-full ${action.color} text-white`}>
                <action.icon className="h-5 w-5" />
              </div>
              <span className="text-xs font-medium text-slate-700 text-center">{action.label}</span>
            </Link>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Upcoming Appointments */}
          <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">
                {lang === 'ar' ? 'المواعيد القادمة' : 'Upcoming Appointments'}
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-teal-600" asChild>
                <Link to="/doctor/appointments">
                  {lang === 'ar' ? 'عرض الكل' : 'View All'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex gap-3">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-3 w-1/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {dashboardData.upcomingAppointments.map(apt => (
                    <div key={apt.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={apt.patient.image} />
                        <AvatarFallback className="bg-teal-100 text-teal-700">
                          {apt.patient.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{apt.patient.name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{apt.type}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {format(apt.time, 'h:mm a', { locale })}
                        </p>
                        <Badge variant="secondary" className={apt.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {apt.status === 'confirmed' ? (lang === 'ar' ? 'مؤكد' : 'Confirmed') : (lang === 'ar' ? 'معلق' : 'Pending')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Reviews */}
          <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">
                {lang === 'ar' ? 'آخر التقييمات' : 'Recent Reviews'}
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-teal-600" asChild>
                <Link to="/doctor/reviews">
                  {lang === 'ar' ? 'عرض الكل' : 'View All'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2].map(i => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="h-4 w-1/3" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboardData.recentReviews.map(review => (
                    <div key={review.id} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-900 dark:text-white">{review.patient}</span>
                        <div className="flex items-center gap-1">
                          {[...Array(review.rating)].map((_, i) => (
                            <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{review.comment}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {format(review.date, 'PPP', { locale })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DoctorDashboard;
